import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ArrowRight, Trash, CalendarBlank } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { RM, RMcompact, MONTHS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Payroll() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/payroll/runs").then(({ data }) => setRuns(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/payroll/runs", { month: parseInt(month), year: parseInt(year) });
      toast.success("Payroll run created");
      setOpen(false);
      navigate(`/payroll/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/payroll/runs/${toDelete.id}`);
      toast.success("Run deleted");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  return (
    <Layout
      title="Payroll Processing"
      subtitle="Create and manage monthly payroll runs"
      actions={
        isAdmin ? (
          <Button data-testid="new-run-btn" onClick={() => setOpen(true)} className="bg-red-500 font-semibold text-white transition-colors duration-200 hover:bg-red-600">
            <Plus size={16} weight="bold" className="mr-1.5" /> New Payroll Run
          </Button>
        ) : null
      }
    >
      {runs.length === 0 ? (
        <div className="flex flex-col items-center justify-center border border-dashed border-[#E2E5EA] bg-[#FFFFFF] py-20 text-center">
          <CalendarBlank size={40} className="text-[#C7CDD6]" />
          <p className="mt-4 font-heading text-lg font-semibold text-[#0F172A]">No payroll runs yet</p>
          <p className="mt-1 max-w-sm text-sm text-[#9CA3AF]">
            Start your first monthly run. Statutory contributions are computed automatically for all active employees.
          </p>
          {isAdmin && (
            <Button onClick={() => setOpen(true)} className="mt-5 bg-red-500 font-semibold text-white hover:bg-red-600">
              <Plus size={16} weight="bold" className="mr-1.5" /> New Payroll Run
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {runs.map((r, i) => (
            <div
              key={r.id}
              data-testid={`run-card-${r.id}`}
              style={{ animationDelay: `${i * 50}ms` }}
              className="group animate-fade-up cursor-pointer border border-[#E2E5EA] bg-[#FFFFFF] p-5 transition-colors duration-200 hover:border-red-500/40"
              onClick={() => navigate(`/payroll/${r.id}`)}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#0F172A]">{MONTHS[r.month]} {r.year}</h3>
                  <p className="text-xs text-[#6B7280]">Pay date {r.pay_date}</p>
                </div>
                <StatusBadge status={r.status} />
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#6B7280]">Net Payroll</p>
                  <p className="font-heading text-lg font-bold tabular-nums text-red-500">{RMcompact(r.summary.net_pay)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#6B7280]">Employees</p>
                  <p className="font-heading text-lg font-bold tabular-nums text-[#0F172A]">{r.summary.employee_count}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-[#E2E5EA] pt-4">
                <span className="flex items-center text-sm text-red-500 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                  Open <ArrowRight size={14} className="ml-1" />
                </span>
                {isAdmin && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setToDelete(r); }}
                    className="rounded p-1.5 text-[#6B7280] hover:bg-[#E2E5EA] hover:text-red-400"
                  >
                    <Trash size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md border-[#E2E5EA] bg-[#FFFFFF] text-[#0F172A]">
          <DialogHeader><DialogTitle className="font-heading text-xl">New Payroll Run</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#9CA3AF]">Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger data-testid="run-month" className="mt-1.5 border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A]"><SelectValue /></SelectTrigger>
                <SelectContent className="border-[#E2E5EA] bg-[#EEF1F5] text-[#0F172A]">
                  {MONTHS.slice(1).map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#9CA3AF]">Year</Label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger data-testid="run-year" className="mt-1.5 border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A]"><SelectValue /></SelectTrigger>
                <SelectContent className="border-[#E2E5EA] bg-[#EEF1F5] text-[#0F172A]">
                  {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5]">Cancel</Button>
            <Button data-testid="create-run" onClick={create} disabled={creating} className="bg-red-500 font-semibold text-white hover:bg-red-600">
              {creating ? "Creating…" : "Create Run"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-[#E2E5EA] bg-[#FFFFFF] text-[#0F172A]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete payroll run?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              This removes {toDelete && `${MONTHS[toDelete.month]} ${toDelete.year}`} and all its computed items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 text-white hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
